<template>
  <div class="vcl-table-tools" tabindex="-1" :data-sticky="stickyHeader" :style="widgetStyles">
    <div class="filters-bar">
      <div v-if="showFilters || $slots.buttons" class="filters-bar__left-panel">
        <div v-if="showFilters" class="filters-bar__left-panel__filters">
          <vp-tooltip ref="tooltip" v-bind="fetchTooltipProps()" @visible="($evt) => updateMenuVisibility($evt)">
            <template #target>
              <vp-button v-if="!disabled" class="vcl-table-tools__dropdown-button no-fill" :styles="{ padding: '0', border: 'none', hoverBorder: 'none', fontWeight: '400', textTransform: 'capitalize'}" ref="target">
                <span>Filters</span>
                <img :src="`https://d1byei05mtwnfj.cloudfront.net/images/icons/shell/arrow-${menuVisible ? 'up' : 'down'}--filled.svg`" alt="">
              </vp-button>
            </template>
            <template #header>
              <div class="vcl-table-tools__dropdown-header">
                <span>Select Filter</span>
              </div>
            </template>
            <template #default>
              <ul class="vcl-table-tools__dropdown">
                <li v-for="item in filters" :key="item.name" class="vcl-table-tools__dropdown-item" v-bind="{'data-added': alreadyAdded[item.name]}" @click="addFilter(item)" :disabled="!item.options.length" :title="(!item.options.length && 'no options available') || (alreadyAdded[item.name] && 'already added')">
                  <span>{{ item.name }}</span>
                </li>
                <li v-if="!filters.length" class="vcl-table-tools__dropdown-item" data-none>--No Filters Available--</li>
              </ul>
            </template>
          </vp-tooltip>
        </div>
        <slot name="buttons" />
      </div>
      <div class="filters-bar__right-panel">
        <span class="vcl-table-tools__loading" v-bind="{ 'data-active': busy }">
          <i class=" fa fa-circle-o-notch fa-spin iconGradient" />
        </span>
        <vp-input v-if="showSearch" class="vcl-table-tools__search" :placeholder="$t('search')" width="250px" height="40px" v-model="tableSearchText" :styles="{ border: '1px solid #AFAFAF' }" @input="setDebounce($event)">
          <template #suffix><VpIcon icon="magnify" style="color: #AFAFAF; font-size: 15px" /></template>
        </vp-input>
      </div>
    </div>
    <div class="filters-active" v-bind="{ 'data-visible': showActiveFilters }">
      <div class="filters-active__left-panel">
        <div v-for="(item, index) in activeFilters" :key="`active-${index}`" class="filters-active__item">
          <vp-tooltip ref="itemTooltip" v-bind="fetchTooltipProps(item)" @visible="toggleMenuSearchFocus(index)">
            <template #target>
              <vp-button class="filters-active__item__button no-fill" :styles="{ border: '1px solid #DBDBDB', hoverBorder: '1px solid #DBDBDB', fontWeight: '400', textTransform: 'capitalize'}">
                <span>{{ item.name }}</span>
                <img src="https://d1byei05mtwnfj.cloudfront.net/images/icons/shell/arrow-down--filled.svg" alt="arrow-down">
              </vp-button>
            </template>
            <template #header>
              <div class="vcl-table-tools__dropdown-header">
                <span>{{item.filterTitle}}</span>
                <div v-if="!item.singleton" class="vcl-table-tools__dropdown-header__controller">
                  <a class="vcl-table-tools__controller" @click="filterToggleAllOptions(item, true)">Select All</a>
                  <span class="vcl-table-tools__controller--spacing">|</span>
                  <a class="vcl-table-tools__controller" @click="filterToggleAllOptions(item, false)">Deselect All</a>
                </div>
              </div>
            </template>
            <template #default>
              <div v-if="item.searchOption" class="active-filters-dropdown__header">
                <vp-input :ref="`item-searchbox-${index}`" class="active-filters-dropdown__search" v-model="searchText" autocomplete="off" :placeholder="$t('search')" height="40px" width="100%" @input="searchUpdate(item, searchText)">
                  <template #suffix><VpIcon icon="magnify" style="color: #AFAFAF; font-size: 15px" /></template>
                </vp-input>
              </div>
              <ul class="vcl-table-tools__dropdown">
                <li v-for="(filter, k) in item.options" v-show="!searchText || !item.searchOption || filter.searchMatch" :key="`filter-${item.name}-${index}-option-${k}`" class="active-filters-dropdown__item" >
                  <vp-checkbox :key="`filter-option-${item.name}-${index}-option-${k}-${filter.name}`" :value="filter.enabled" :styles="{ size: 16, labelColor: '#016699' }" :disabled="filter.applied || filter.disabled" :title="(filter.applied && 'option already applied') || (filter.disabled && 'option disabled')" @input="(value) => filterChange(value, filter, item)" >
                    <template #label>{{ filter.name }}</template>
                  </vp-checkbox>
                </li>
                <li v-if="!item.options.length" class="active-filters-dropdown__item" data-none>--No Options Available--</li>
              </ul>
              <div v-show="!item.applied && item.options.length" class="active-filters-dropdown__footer">
                <vp-button :styles="{ hoverBorder: '#016699 2px solid' }" @click="applyFilters(item, index)">Apply Filters</vp-button>
              </div>
            </template>
          </vp-tooltip>
        </div>
        <div v-for="(item, index) in value" :key="`active-value-${index}`" class="filters-active__item" ref="filter">
          <vp-button class="filters-active__item__button" :styles="{ border: '1px solid #A7CCE1', hoverBorder: '1px solid #A7CCE1', backgroundColor: '#E5F0F6', color: '#1B3664', fontWeight: '400', textTransform: 'capitalize'}" @click="clearActiveFilter(item)">
            <span>{{ item.name }}: {{ item.value }}<vp-icon class="close" title="clear" icon="close" /></span>
          </vp-button>
        </div>
      </div>
      <div v-if="showActiveFilters && value.length" class="filters-active__right-panel">
        <a class="vcl-table-tools__controller" @click="filterClearAll()">Clear Filters</a>
      </div>
    </div>
    <slot name="content" />
  </div>
</template>

<script>
import VpTooltip from '../vp-tooltip/VpTooltip.vue';
import VpInput from '../vp-input/VpInput.vue';
import VpButton from '../vp-button/VpButton.vue';
import VpIcon from '../vp-icon/VpIcon.vue';

export default {
  name: 'VpTableTools',
  components: {
    VpTooltip,
    VpInput,
    VpButton,
    VpIcon,
  },
  props: {
    searchDebounce: {
      type: Number,
      default: 0,
    },
    search: {
      type: String,
      default: ''
    },
    value: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Array,
      default: () => [],
    },
    busy: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showFilters: {
      type: [Number, Boolean],
      default: true,
    },
    showSearch: {
      type: [Number, Boolean],
      default: true,
    },
    styles: {
      type: Object,
      default: () => ({}),
    },

    /** VpTable Sticky Props */
    table: {
      type: [HTMLElement, Object],
      default: null,
      validator: (table) => {
        const isValid = table && table._isVue;
        if (!isValid) console.error('Invalid Prop, Table must be a reference to VpTable component');
        return isValid;
      },
    },
    stickyHeader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      menuVisible: false,
      visible: false,
      activeFilters: [],
      alreadyAdded: {},
      ready: false,
      debounce: null,
      searchText: '',
      tableSearchText: this.search,
    };
  },
  emits: ['input', 'search'],
  mounted() {
    this.updateFromModel(this.value);
    this.ready = true;
  },
  destroyed() {
    clearTimeout(this.debounce);
    clearTimeout(this.debounceAnimation);
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        this.updateFromModel(value);
      },
    },
    search: {
      immediate: true,
      handler(search) {
        this.tableSearchText = search;
      },
    },
    showActiveFilters() {
      /**
       * Emit event based on toggle state of active filters row
       * Used for recalculating Sticky Header Heights
       * Timeout below is based on transition timer in stylesheet
      */
      const animationTimeout = 501;
      /** Force Sync  */
      if (!this.stickyHeader) return;
      this.debounceAnimation = setTimeout(() => {
        if (this.$parent.mixinSetHeight) {
          return this.$parent.mixinSetHeight();
        }
        return this.table && this.table.mixinSetHeight();
      }, animationTimeout);
    },
  },
  computed: {
    showActiveFilters() {
      const hasActiveOpts = Object.keys(this.alreadyAdded).some((key) => this.alreadyAdded[key]);
      return this.activeFilters.length > 0 || this.value.length > 0 || hasActiveOpts;
    },
    widgetStyles() {
      const { top, backgroundColor, borderBottom, borderTop, index } = this.styles || {};
      const defaultBorderBottom = borderBottom || this.stickyHeader ? '1px solid #DBDBDB' : 'unset'
      return {
        '--tools-top': top || 'unset',
        '--tools-index': index || 'unset',
        '--tools-background': backgroundColor || '#fff',
        '--tools-border-top': borderTop || '1px solid #DBDBDB',
        '--tools-border-bottom': defaultBorderBottom,
      };
    },
  },
  methods: {
    fetchTooltipProps(item) {
      const opts = {
        styles: {
          width: 'unset',
          minWidth: '200px',
          maxWidth: 'unset',
          borderRadius: '6px',
          contentBorderRadius: '0 0 6px 6px',
          arrow: {
            top: { color: 'transparent', border: 'none' },
            bottom: { color: 'transparent', border: 'none' },
          },
        },
        enableScrolling: true,
        openBy: 'click',
        placement: 'bottom-start',
        offset: [0, 12],
      };
      if (item) {
        opts.styles.maxHeight = '378px';
        opts.offset = [0, 8];
        if (item.searchOption) {
          opts.styles.minWidth = '250px';
        }
      }
      return opts;
    },
    updateMenuVisibility(bool = false) {
      this.menuVisible = bool;
    },
    setDebounce($event = '') {
      const value = $event ? String($event).trim() : '';
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.$emit('search', value), this.searchDebounce);
    },
    filterChange(enabled = false, filter = {}, all = {}) {
      const currentSet = this.activeFilters.find(i => i.name === all.name);
      return currentSet && currentSet.options.forEach((item) => {
        if (item.name === filter.name && item.id === filter.id) this.$set(item, 'enabled', enabled);
        else if (all.singleton) this.$set(item, 'enabled', enabled ? false : item.enabled);
        else this.$set(item, 'enabled', item.applied ? false : item.enabled);
      });
    },
    filterClearAll() {
      this.alreadyAdded = {};
      this.activeFilters = [];
      this.$emit('input', []);
    },
    filterToggleAllOptions(filter, bool = false) {
      if (!filter || filter.singleton) return null;
      filter.options.forEach((item) => {
        item.enabled = !item.applied && !item.disabled ? bool : false;
      });
    },
    updateFromModel(list = []) {
      list.forEach(({ name }) => {
        const template = this.filters.find(i => i.name === name);
        if (template.singleton) {
          this.alreadyAdded[name] = true;
        }
      });
      /** Filter alreadyAdded keys */
      for (const key in this.alreadyAdded) {
        const exist = list.find(({ name }) => name === key);
        if (!exist) delete this.alreadyAdded[key];
      }
    },
    toggleFilters() {
      this.visible = !this.visible;
    },
    toggleMenu(index) {
      const { tooltip, itemTooltip } = this.$refs;
      if (index !== undefined) {
        return itemTooltip && itemTooltip[index] && itemTooltip[index].toggleOff();
      }
      return tooltip && tooltip.toggleOff();
    },
    toggleMenuSearchFocus(index) {
      const target = this.$refs[`item-searchbox-${index}`];
      if (index !== undefined && target) {
        target[0] && target[0].$nextTick(() => setTimeout(() => target[0].focus()));
      }
    },
    addFilter(item = {}) {
      if (!item.options.length) return 'no options available';
      if (this.alreadyAdded[item.name]) return 'option already added';
      this.toggleMenu();
      const filter = item;
      const exist = this.activeFilters.find((_filter) => _filter.name === item.name);
      if (!this.alreadyAdded[item.name] && !exist) {
        filter.options = item.options
          .map((option) => {
            const applied = this.value.some((current) => {
              const value = current.id !== undefined ? current.id : current.value;
              const filterNameExist = current.name === item.name;
              const filterValueExist = [option.id, option.name, option].includes(value);
              return filterNameExist && filterValueExist;
            });
            if (typeof option === 'object') {
              return { ...option, enabled: false, applied };
            }
            return { name: option, enabled: false, applied };
          })
        filter.applied = false;
        this.activeFilters.push(filter);
        if (item.singleton || this.activeFilters.find((el) => el.name === item.name)) {
          this.alreadyAdded[filter.name] = true;
        }
      }
      this.toggleFilters();
    },
    applyFilters(item = {}, index) {
      item.applied = true;
      const template = this.filters.find(({ name }) => item.name === name);
      if (template.singleton || this.activeFilters.find((el) => el.name === item.name)) {
        this.alreadyAdded[template.name] = false;
      }
      this.toggleMenu(index);
      this.removeActiveFilter(item);
      const newValues = item.options.reduce((filters, { name: value, id, enabled }) => {
        if (enabled) {
          const exist = filters.find((filter) => item.name === filter.name && value === filter.value);
          if (!exist) filters.unshift({ name: item.name, value, id });
        }
        return filters;
      }, this.value);
      this.$emit('input', newValues);
      this.searchText = '';
    },
    removeActiveFilter(item = {}) {
      this.activeFilters = this.activeFilters.filter((_item) => _item !== item);
    },
    clearActiveFilter(item = {}) {
      const template = this.filters.find(({ name }) => item.name === name);
      if (template.singleton) {
        this.alreadyAdded[template.name] = false;
      }
      this.removeActiveFilter(item);
      const newValues = this.value.filter((filter) => {
        return item.name !== filter.name || item.value !== filter.value;
      });
      this.$emit('input', newValues);
    },
    searchUpdate(item = {}, searchText = '') {
      item.options.forEach((opt) => {
        opt.searchMatch = opt.name.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
$backgroundgray:  #F4F7F8;
$lightgray: #DBDBDB;
$linkblue: #016699;
$charcoal: #4F4F4F;
$charcoal-grey: #40454a;
.vcl-table-tools {
  background-color: var(--tools-background);
  border-bottom: var(--tools-border-bottom) !important;
  border-top: var(--tools-border-top) !important;
  &[data-sticky] {
    position: sticky;
    top: var(--tools-top);
    z-index: var(--tools-index);
  }
  &__controller, &__controller--spacing {
    cursor: pointer;
    font-size: 14px;
    color: $linkblue;
    text-transform: capitalize;
    text-decoration: underline;
  }
  &__controller--spacing {
    cursor: unset;
  }
  &__dropdown {
    margin: 0;
    padding: 0;
    list-style-type: none;
    border-radius: 0 0 6px 6px;
    &-button {
      gap: 0.25rem;
      display: flex;
      padding-left: 0 !important;
      align-items: center;
      justify-content: start;
      font-size: 1rem !important;
      min-width: unset !important;
      &:hover {
        filter: none !important;
      }
      img {
        filter: brightness(0);
      }
    }
    &-header {
      gap: 1rem;
      height: 38px;
      display: flex;
      padding: 0 1rem;
      font-size: 14px;
      text-align: left;
      font-weight: 600;
      align-items: center;
      text-transform: uppercase;
      border-radius: 6px 6px 0 0;
      background: $backgroundgray;
      justify-content: space-between;
      border-bottom: 1px solid $lightgray;
      &__controller {
        display: flex;
        gap: 0.25rem;
      }
    }
    &-item {
      height: 43px;
      margin: 2px 0;
      display: flex;
      padding: 0 1rem;
      cursor: pointer;
      align-items: center;
      border-bottom: 1px solid $lightgray;
      font-size: 1rem;
      &[data-none] {
        pointer-events: none;
      }
      &:last-child {
        margin-bottom: 1rem;
      }
      &, span {
        transition: color 100ms, background-color 100ms;
      }
      span {
        color: $linkblue !important;
      }
      &:hover {
        background-color: $linkblue;
        & > span {
          color: white !important;
        }
      }
      &[data-added]:hover, &[disabled] {
        cursor: not-allowed;
        background: #FDFDFD;
        & > span{
          color: $linkblue !important;
        }
      }
    }
  }
  &__loading {
    opacity: 0;
    min-width: 1rem;
    &[data-active] {
      opacity: 1;
    }
  }
  &__search {
    justify-self: flex-end;
  }
  .filters-bar {
    justify-content: space-between;
    &, & > div {
      display: flex;
      min-height: 55px;
    }
    & > div span {
      color: $charcoal;
      letter-spacing: normal;
    }
    &__left-panel {
      border-right: 1px solid $lightgray;
      &__filters {
        min-width: 100px;
        max-width: 100px;
      }
      & > * {
        display: flex;
        padding: 0 1rem;
        align-items: center;
        &:first-child {
          padding-left: 1.25rem;;
        }
        &::v-deep .vcl-slider-toggle {
          label {
            margin: 0;
          }
        }
      }
      & > *:nth-child(n+2) {
        border-left: 1px solid lightgrey;
      }
    }
    &__right-panel {
      gap: 1rem;
      flex-grow: 1;
      padding: 0 1.25rem;
      align-items: center;
      justify-content: space-between;
    }
  }

  .filters-active {
    display: flex;
    overflow: hidden;
    background: white;
    /** Animation */
    padding: 0;
    opacity: 0;
    min-height: 0;
    transition: all 500ms ease-in-out;
    &[data-visible] {
      opacity: 1;
      min-height: 50px;
      padding: .25rem 1.5rem;
      border-top: 1px $lightgray solid;
    }
    /** End of animation setup */
    &__left-panel {
      flex: 1;
      gap: .5rem;
      display: flex;
      flex-wrap: wrap;
      overflow: auto;
      /** Max Height for active filters; 2.5 max rows, 35px item height, .5rem padding offset from parent; */
      max-height: calc((35px * 2.5) + .5rem);
      align-items: center;
    }
    &__right-panel {
      height: 35px;
      display: flex;
      align-items: center;
      justify-self: flex-end;
    }
    .filters-active__item {
      display: contents;
      &__button {
        @extend .vcl-table-tools__dropdown-button;
        height: 35px;
        font-size: 14px !important;
        padding: .5rem !important;
        &:hover {
          filter: unset;
        }

        .close {
          font-size: 8px;
          margin-left: 8px;
          position: relative;
          color: #016699;
        }
      }

      .active-filters-dropdown {
        padding: 0;
        &__header, &__footer {
          z-index: 1;
          display: flex;
          position: sticky;
          background: white;
          align-items: center;
          padding: .25rem 1rem;
          justify-content: center;
          background-color: #FFFFFF;
        }
        &__header {
          top: 0;
          min-height: 56px;
          border-bottom: 1px solid $lightgray;
        }
        &__footer {
          bottom: 0;
          font-size: 1rem;
          min-height: 46px;
          border-top: 1px solid $lightgray;
          & > button {
            width: 150px;
            min-width: unset;
          }
        }
        &__item {
          display: flex;
          align-items: center;
          height: 42px;
          padding: 0 1rem;
          font-size: 16px;
          border-bottom: 1px solid $lightgray;
          &[data-none] {
            pointer-events: none;
            color: $charcoal-grey
          }
          &, &::v-deep .vcl-checkbox__label {
            color: $linkblue;
            &.disabled {
              color: $lightgray;
            }
          }
          &:last-child {
            padding-bottom: 0;
            border-bottom: none;
          }
        }
      }
    }
  }
}
</style>
